import { useState } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import { RetainerSelector, OUTSIDE_ODO_ID, useEscapeKey } from '@odo/shared-ui';

interface ProceedRetentionModalProps {
    open: boolean;
    patientName: string;
    isSubmitting: boolean;
    onSubmit: (retainerOption: string) => void;
    onClose: () => void;
}

export function ProceedRetentionModal({
    open,
    patientName,
    isSubmitting,
    onSubmit,
    onClose
}: ProceedRetentionModalProps) {
    const [selectedRetainer, setSelectedRetainer] = useState<string | null>(null);
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [feeAcknowledged, setFeeAcknowledged] = useState(false);

    useEscapeKey(onClose, open && !isSubmitting);

    if (!open) return null;

    const handleSelectRetainer = (id: string): void => {
        setSelectedRetainer(id);
        setFeeAcknowledged(false);
    };

    const isOutsideOdo = selectedRetainer === OUTSIDE_ODO_ID;
    const showFeeNotice = selectedRetainer !== null && (!isOutsideOdo || disclaimerAccepted);
    const canSubmit = showFeeNotice && feeAcknowledged && !isSubmitting;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="proceed-retention-title"
            className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
        >
            <div className="w-full max-w-[500px] bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="px-5 py-3.5 border-b border-gray-200 flex items-center gap-3 flex-shrink-0">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-100">
                        <CheckIcon className="w-4 h-4 text-gray-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div
                            id="proceed-retention-title"
                            className="text-sm font-bold text-gray-900"
                        >
                            Confirm retention
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 truncate">
                            {patientName || 'Patient'}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        aria-label="Close"
                        className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors disabled:opacity-50"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-5 py-4 flex flex-col gap-4 overflow-y-auto">
                    <div>
                        <p className="text-xs font-semibold text-gray-700 mb-2">Select retainers</p>
                        <RetainerSelector
                            layout="grid"
                            selectedRetainer={selectedRetainer}
                            onSelect={handleSelectRetainer}
                            disclaimerAccepted={disclaimerAccepted}
                            onDisclaimerChange={setDisclaimerAccepted}
                        />
                    </div>

                    {showFeeNotice && (
                        <FeeNoticeCard
                            feeAcknowledged={feeAcknowledged}
                            onFeeAcknowledged={setFeeAcknowledged}
                        />
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-4 py-2 rounded-md border border-gray-300 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => canSubmit && onSubmit(selectedRetainer)}
                        disabled={!canSubmit}
                        className="px-4 py-2 rounded-md text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 bg-gray-900 text-white hover:bg-gray-800"
                    >
                        {isSubmitting && <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />}
                        Confirm retention
                    </button>
                </div>
            </div>
        </div>
    );
}

interface FeeNoticeCardProps {
    feeAcknowledged: boolean;
    onFeeAcknowledged: (v: boolean) => void;
}

function FeeNoticeCard({ feeAcknowledged, onFeeAcknowledged }: FeeNoticeCardProps) {
    return (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex gap-4">
            <ExclamationTriangleIcon className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
                <p className="text-sm font-bold text-gray-800 mb-2">Re-treatment fee notice</p>
                <p className="text-sm text-gray-700">
                    Refinements requested after retention incur a{' '}
                    <span className="font-bold">$500</span> re-treatment fee (new aligners &amp;
                    treatment plan reassessment), provided the case is still within ODO&apos;s
                    treatment term. Outside that term, standard new case fees apply (aligners +
                    Dental Monitoring).
                </p>
                <label className="flex items-start gap-2.5 mt-4 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={feeAcknowledged}
                        onChange={(e) => onFeeAcknowledged(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-800 focus:ring-gray-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">
                        I acknowledge and accept the re-treatment fee terms described above.
                    </span>
                </label>
            </div>
        </div>
    );
}
